import { ExternalLink, FileText } from "lucide-react";

import type { FiscalDocumentInput } from "@/domain/fiscal-documents";
import { FinancePresentation } from "@/presentation/finance";

interface FiscalDocument {
	id: string;
	type: "NFE" | "NFSE";
	number: string;
	issuerName: string;
	issuedOn: string;
	competenceOn: string | null;
	totalCents: number;
	payableCents: number;
	items: FiscalDocumentInput["items"];
	installments: FiscalDocumentInput["installments"];
	installmentNumber?: string | null;
	matchBasis?: string;
}

export function TransactionFiscalDocuments({
	documents,
	showEmpty = false,
	linkExplanation = "Ligação automática por CNPJ do fornecedor, valor exato e janela documental; o pagamento continua sendo o fato bancário da Pluggy.",
	emptyExplanation = "Nenhum documento fiscal foi ligado a este pagamento no Qive. Apólices, faturas e outros documentos comerciais continuam podendo explicar a despesa.",
}: {
	documents: FiscalDocument[];
	showEmpty?: boolean;
	linkExplanation?: string;
	emptyExplanation?: string;
}) {
	if (documents.length === 0) {
		if (!showEmpty) {
			return null;
		}

		return (
			<div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-4 py-4 text-xs leading-5 text-stone-500">
				{emptyExplanation}
			</div>
		);
	}

	return (
		<div className="space-y-3">
			{documents.map((document) => (
				<FiscalDocumentCard
					key={document.id}
					document={document}
					linkExplanation={linkExplanation}
				/>
			))}
		</div>
	);
}

function FiscalDocumentCard({
	document,
	linkExplanation,
}: {
	document: FiscalDocument;
	linkExplanation: string;
}) {
	const title = document.type === "NFE" ? "NF-e" : "NFS-e";
	const auxiliaryDocument = document.type === "NFE" ? "DANFE" : "DANFSe";

	return (
		<article className="rounded-2xl border border-stone-200 bg-white p-4">
			<div className="flex items-start justify-between gap-4">
				<div className="flex min-w-0 items-start gap-3">
					<span className="grid size-9 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-800">
						<FileText className="size-4" />
					</span>
					<div className="min-w-0">
						<p className="text-xs font-semibold text-stone-900">
							{title} nº {document.number}
						</p>
						<p className="mt-0.5 truncate text-[11px] text-stone-500">{document.issuerName}</p>
					</div>
				</div>
				<a
					href={`/api/fiscal-document.pdf?id=${encodeURIComponent(document.id)}`}
					target="_blank"
					rel="noreferrer"
					className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-stone-200 px-3 py-2 text-xs font-semibold text-emerald-900 transition hover:bg-emerald-50"
				>
					Ver {auxiliaryDocument}
					<ExternalLink className="size-3.5" />
				</a>
			</div>

			<div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
				<DocumentFact
					label="Emissão"
					value={FinancePresentation.transactionDate(document.issuedOn)}
				/>
				{document.competenceOn && (
					<DocumentFact
						label="Competência"
						value={FinancePresentation.transactionDate(document.competenceOn)}
					/>
				)}
				<DocumentFact label="Total" value={FinancePresentation.money(document.totalCents)} />
				{document.payableCents !== document.totalCents && (
					<DocumentFact
						label="Valor líquido"
						value={FinancePresentation.money(document.payableCents)}
					/>
				)}
			</div>

			<div className="mt-3 rounded-xl bg-stone-50 px-3 py-2.5">
				<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">
					Compra ou serviço
				</p>
				<ul className="mt-1.5 space-y-1 text-xs leading-5 text-stone-700">
					{document.items.slice(0, 4).map((item, index) => (
						<li key={`${item.description}-${index}`}>
							{item.description}
							{item.quantity && item.unitPrice
								? ` · ${FinancePresentation.decimal(item.quantity)} × R$ ${FinancePresentation.decimal(item.unitPrice)}`
								: ""}
						</li>
					))}
				</ul>
				{document.items.length > 4 && (
					<p className="mt-1 text-[11px] text-stone-500">
						+ {document.items.length - 4} itens no documento
					</p>
				)}
			</div>

			{document.installments.length > 0 && (
				<div className="mt-3 rounded-xl border border-stone-200 px-3 py-2.5">
					<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">
						Parcelas declaradas na NF-e
					</p>
					<ul className="mt-2 grid gap-1.5 text-xs text-stone-700 sm:grid-cols-2">
						{document.installments.map((installment, index) => {
							const selected =
								!!document.installmentNumber && document.installmentNumber === installment.number;

							return (
								<li
									key={`${installment.number ?? index}-${installment.dueOn ?? "sem-data"}`}
									className={`flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 ${
										selected ? "bg-emerald-50 font-semibold text-emerald-900" : "bg-stone-50"
									}`}
								>
									<span>
										{installment.number ? `Parcela ${installment.number}` : `Parcela ${index + 1}`}
										{installment.dueOn
											? ` · ${FinancePresentation.transactionDate(installment.dueOn)}`
											: " · sem vencimento declarado"}
									</span>
									<span className="shrink-0 tabular-nums">
										{FinancePresentation.money(installment.amountCents)}
									</span>
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{document.matchBasis === "BALANCED_INSTALLMENT_GROUP" && (
				<p className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-[11px] leading-5 text-amber-900">
					O grupo completo de boletos fecha CNPJ, emissão, quantidade e valores das parcelas da
					NF-e. Os vencimentos informados pelo boleto e pelo XML divergem e permanecem visíveis como
					fatos distintos.
				</p>
			)}

			<p className="mt-2 text-[11px] leading-4 text-stone-400">{linkExplanation}</p>
		</article>
	);
}

function DocumentFact({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</p>
			<p className="mt-0.5 font-semibold text-stone-700">{value}</p>
		</div>
	);
}
